import { Product as D } from './productTokens';
import { buildPaymentMethodFeeComparison } from './paymentMethodFeeCompareCalc';

function money(value) {
  const n = Number(value) || 0;
  return `R ${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Side-by-side Card vs Ozow cost comparison, shown BEFORE the buyer commits
 * to a payment method, so the amount updates immediately when the method
 * changes. Reads a three-tier rate (see paymentMethodFeeCompareCalc.js):
 * account-CONFIRMED > the payment provider's own published-but-unverified
 * DOCUMENTED rate > unknown.
 *
 * Only the confirmed tier (reconciled to the cent against a real
 * transaction) is ever labelled "You pay" — the documented tier's formula
 * does not yet reconcile against the one known real production total (see
 * paymentMethodFeeCompareCalc.js for the numbers), so that figure is
 * explicitly presented as "Published-rate estimate" with a caveat, never as
 * an exact charge. The unknown tier (neither exists) falls back to
 * "Confirmed at secure checkout" with no number at all, matching the rest
 * of the app's
 * pre-payment screens.
 */
export default function PaymentMethodFeeCompare({
  unknownFeeCopy = 'Exact charges confirmed at secure checkout',
  itemAmount,
  trustTradeFee,
  trustTradeFeePercent,
  baseBeforeProvider,
  confirmedRates,
  documentedRates,
  estimateReconciled,
  quickSettlementFee,
  estimateEligible = false,
  vatPercent,
  selectedMethod,
  onSelect,
  methods = ['CARD', 'OZOW'],
}) {
  // TrustTrade's own fee is OUR fee, not the provider's — always exact and
  // disclosed with its percentage, unlike the provider's processing fee
  // (which only ever shows a number once a confirmed/documented rate
  // exists — see buildPaymentMethodFeeComparison). trustTradeFeePercent is
  // optional so this still renders sensibly if a caller doesn't pass it.
  const trustTradeFeeLabel = trustTradeFeePercent != null
    ? `TrustTrade fee (${trustTradeFeePercent}%)`
    : 'TrustTrade fee';
  // Conditional quick-settlement fixed charge per provider transaction
  // (backend provider_quick_settlement_fee): amount excl. VAT x the number of
  // payee parties it applies to. Unknown/missing -> null, which keeps the
  // documented tier unquantified.
  const fixedAmount = Number(quickSettlementFee?.amount_excl_vat);
  const fixedParties = Number(quickSettlementFee?.charged_party_count);
  const fixedFeeExclVat = Number.isFinite(fixedAmount) && Number.isFinite(fixedParties)
    ? fixedAmount * fixedParties
    : null;
  const rows = buildPaymentMethodFeeComparison({
    baseBeforeProvider, confirmedRates, documentedRates, estimateReconciled,
    fixedFeeExclVat, estimateEligible, vatPercent, methods,
  });

  return (
    <div
      data-testid="payment-method-fee-compare"
      role="radiogroup"
      aria-label="Choose payment method"
      style={{ display: 'grid', gridTemplateColumns: methods.length > 1 ? 'repeat(auto-fit, minmax(220px, 1fr))' : '1fr', gap: 12 }}
    >
      {rows.map((row) => {
        const methodKey = row.method.toLowerCase();
        const selected = selectedMethod && String(selectedMethod).toLowerCase() === methodKey;
        return (
          <button
            type="button"
            key={row.method}
            role="radio"
            aria-checked={Boolean(selected)}
            data-testid={`fee-compare-card-${methodKey}`}
            data-tier={row.tier}
            onClick={onSelect ? () => onSelect(methodKey) : undefined}
            disabled={!onSelect}
            style={{
              border: `1.5px solid ${selected ? D.accent : D.border}`,
              borderRadius: 12,
              padding: '14px 16px',
              cursor: onSelect ? 'pointer' : 'default',
              background: selected ? 'rgba(47,155,255,0.10)' : '#101317',
              transition: 'all 0.15s',
              width: '100%',
              textAlign: 'left',
              font: 'inherit',
              appearance: 'none',
              opacity: 1,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, gap: 8 }}>
              <strong style={{ fontSize: 14, color: D.text }}>{row.label}</strong>
              {row.cheaper && (
                <span
                  data-testid={`fee-compare-cheaper-${methodKey}`}
                  style={{ fontSize: 10, fontWeight: 700, color: D.success, background: 'rgba(16,185,129,0.14)', padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap' }}
                >
                  Lower processing fee
                </span>
              )}
            </div>
            <p style={{ fontSize: 11, color: D.dim, margin: '0 0 10px', lineHeight: 1.4 }}>
              {row.tier === 'unknown' ? unknownFeeCopy : `Processing rate: ${row.providerFeePercent}% + VAT`}
            </p>
            <FeeRow label="Item value" value={money(itemAmount)} />
            <FeeRow label={trustTradeFeeLabel} value={money(trustTradeFee)} />
            {row.tier !== 'unknown' && <FeeRow label={`Processing fee (${row.providerFeePercent}% + VAT)`} value={money(row.processingFeeAmount)} />}
            {row.tier !== 'unknown' && row.settlementFeeAmount != null && <FeeRow label={`Payout settlement fee (${money(row.settlementFeeExclVat)} + VAT)`} value={money(row.settlementFeeAmount)} />}
            <div style={{ borderTop: `1px solid ${D.border}`, marginTop: 8, paddingTop: 8 }}>
              <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: D.text, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {row.youPayLabel}
              </span>
              <span data-testid={`fee-compare-total-${methodKey}`} style={{ display: 'block', fontSize: 18, fontWeight: 800, color: D.success, marginTop: 2 }}>
                {money(row.youPay)}
              </span>
              {row.youPayCaveat && (
                <span data-testid={`fee-compare-caveat-${methodKey}`} style={{ display: 'block', fontSize: 10, color: D.dim, marginTop: 6, lineHeight: 1.4 }}>
                  {row.youPayCaveat}
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function FeeRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: D.sub, marginBottom: 4 }}>
      <span>{label}</span>
      <span style={{ color: D.text, fontWeight: 500 }}>{value}</span>
    </div>
  );
}
